/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : CustomTabsV3.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */

import { Box, Tabs, Tab, Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useState } from "react";
function CustomTabsV3({
  tabs,
  defaultTab = 0,
  onTabChange,
  rightHeading = null,
  leftHeading = null,
}) {
  const [value, setValue] = useState(defaultTab);
  const handleChange = (event, newValue) => {
    setValue(newValue);
    if (onTabChange) {
      onTabChange(newValue);
    }
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={styles.tabsContainer}>
        <Grid container>
          {leftHeading && (
            <Grid item sm={leftHeading?.frame || 0}>
              {leftHeading?.content}
            </Grid>
          )}
          <Grid
            item
            sm={12 - (rightHeading?.frame || 0) - (leftHeading?.frame || 0)}
          >
            <Tabs
              value={value}
              onChange={handleChange}
              textColor="inherit"
              TabIndicatorProps={{
                style: { backgroundColor: "#4caf50" },
              }}
              sx={styles.tabs}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  {...a11yProps(index)}
                  label={
                    <span style={styles.tabLabelContainer}>
                      {tab.name}
                      {tab?.count !== "" ? (
                        <span style={styles.countBadge}>{tab.count}</span>
                      ) : null}
                    </span>
                  }
                />
              ))}
            </Tabs>
          </Grid>
          {rightHeading && (
            <Grid item sm={rightHeading?.frame || 0}>
              {rightHeading?.content}
            </Grid>
          )}
        </Grid>
      </Box>

      {tabs.map((tab, index) => (
        <CustomTabPanel key={index} value={value} index={index}>
          {tab.content}
        </CustomTabPanel>
      ))}
    </Box>
  );
}
export default CustomTabsV3;

function CustomTabPanel({ children, value, index }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

CustomTabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

CustomTabsV3.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      count: PropTypes.number,
      content: PropTypes.node,
    })
  ).isRequired,
  defaultTab: PropTypes.number,
  onTabChange: PropTypes.func,
};

const styles = {
  tabsContainer: {
    borderBottom: 1,
    borderColor: "divider",
  },
  tabs: {
    "& .MuiTab-root": {
      color: "#000000ff",
      textTransform: "none",
    },
    "& .Mui-selected": {
      color: "#000000ff !important",
    },
  },
  tabLabelContainer: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  countBadge: {
    backgroundColor: "#D0D0CE",
    color: "black",
    width: "30px",
    height: "30px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "12px",
    fontWeight: "bold",
    padding: "5px",
  },
  panelBox: {
    p: 3,
  },
};
